import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import type { ThemeColours } from 'fym-shared/src/music24/releaseThemes';
import type { ProfileImageSpec } from 'fym-shared/src/music24/artistProfile';
import { profileImageWatermark } from 'fym-shared/src/music24/story/watermark';
import { isProfileTextKind } from 'fym-shared/src/music24/profileBanner';
import { TypedMemo } from '../../../../common/types';
import { WatermarkLockup } from '../../studio/frames/WatermarkLockup';
import { drawnGroundKind, FrameGround, type HeaderPreviewGround } from '../../studio/spotifyHeader/FrameGround';
import { BannerTextLayer, type BannerTextPreview } from './BannerTextLayer';
import { pressRatioLabel, profileGuides, profilePreviewScale, type PxBox } from './profileFrameGeometry';
import { profileToolMessages as messages } from '../tools/profileToolMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Profile; each part uses its own slice of it */
import styles from '../Profile.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ProfileImageFrameProps {
  spec: ProfileImageSpec;
  /** The width of the preview in px. */
  width: number;
  ground: HeaderPreviewGround;
  colours: ThemeColours;
  /** The green box, the TV bands of YouTube. Never in the file. */
  guides?: boolean;
  /** The round photo that Facebook draws bottom left (its url, or null for an empty circle); absent: none. */
  sitePhotoUrl?: string | null;
  /** The artist photo: the circle that every site cuts. False: the square file. */
  round?: boolean;
  /** A Free preview that is not made yet: the Music24 mark where the render puts it. */
  watermark?: boolean;
  /** The file is on its way: the frame shimmers. */
  loading?: boolean;
  /** A banner: the text that the render puts on it (design profile-card b). */
  bannerText?: BannerTextPreview | null;
  className?: string;
  testId?: string;
}

const boxStyle = (box: PxBox): React.CSSProperties => ({
  left: box.left,
  top: box.top,
  width: box.width,
  height: box.height,
});

/**
 * One artist-level profile image (design artist-profile `ProfileFrame`): the file drawn at file
 * pixels (`PROFILE_IMAGE_SPECS`) and scaled into `width`, with the ground and the crop of the
 * render (`FrameGround`, the subject box of the spec), the Free mark at `profileImageWatermark`, and
 * the guides of the site. The guides and the site photo show only in the preview, never in the file.
 */
const ProfileImageFrameComponent: React.FC<ProfileImageFrameProps> = ({
  spec,
  width,
  ground,
  colours,
  guides = false,
  sitePhotoUrl,
  round = false,
  watermark = false,
  loading = false,
  bannerText = null,
  className,
  testId,
}) => {
  const intl = useIntl();
  const { scale, height } = profilePreviewScale(spec, width);
  const frame = useMemo(() => ({ width: spec.width, height: spec.height }), [spec.height, spec.width]);
  const layout = useMemo(() => profileGuides(spec), [spec]);
  const mark = useMemo(
    () => (watermark ? profileImageWatermark(frame, spec.watermark, spec.width) : null),
    [frame, spec.watermark, spec.width, watermark]
  );
  // The guides are drawn at file pixels: these keep them at 1.5 px and 8 px on screen.
  const line = 1.5 / scale;
  const labelSize = 8 / scale;
  const label =
    layout.label === 'everyScreen'
      ? intl.formatMessage(messages.guideEveryScreen)
      : layout.label === 'subject'
        ? intl.formatMessage(messages.guideSubject)
        : pressRatioLabel(spec.variant);

  return (
    <div
      className={clsx(styles.frame, round && styles.frameRound, className)}
      style={{ width, height }}
      data-testid={testId}
      data-kind={spec.kind}
      data-variant={spec.variant}
      data-ground={drawnGroundKind(ground)}
      // The card or the tool names the image next to it: the frame itself is a picture of it.
      aria-hidden="true"
    >
      <div className={styles.canvas} style={{ width: spec.width, height: spec.height, transform: `scale(${scale})` }}>
        <FrameGround frame={frame} subject={spec.subject} ground={ground} colours={colours} />
        {bannerText !== null && isProfileTextKind(spec.kind) && (
          <BannerTextLayer kind={spec.kind} size={frame} preview={bannerText} colours={colours} />
        )}
        {mark !== null && <WatermarkLockup lockup={mark.lockup} tone="dark" x={mark.x} y={mark.y} />}
        {guides &&
          layout.bands.map((band) => (
            <div key={`${band.top}`} className={styles.band} style={boxStyle(band)} data-testid="profile-guide-band" />
          ))}
        {guides && layout.safe !== null && (
          <div
            className={styles.safe}
            style={{ ...boxStyle(layout.safe), borderWidth: line }}
            data-testid="profile-guide-safe"
          >
            <span className={styles.safeLabel} style={{ fontSize: labelSize, left: 4 / scale, top: 3 / scale }}>
              {label}
            </span>
          </div>
        )}
        {sitePhotoUrl !== undefined && layout.sitePhoto !== null && (
          <span
            className={styles.sitePhoto}
            data-testid="profile-site-photo"
            style={{
              left: layout.sitePhoto.left,
              top: layout.sitePhoto.top,
              width: layout.sitePhoto.size,
              height: layout.sitePhoto.size,
              borderWidth: 3 / scale,
              backgroundImage: sitePhotoUrl === null ? undefined : `url("${sitePhotoUrl}")`,
            }}
          />
        )}
      </div>
      {loading && <div className={styles.shimmer} />}
    </div>
  );
};

export const ProfileImageFrame = TypedMemo(ProfileImageFrameComponent);
